/* Piece Verse product gallery: syncs scroll-snap slides, thumbnails and variant changes. */
if (!customElements.get('bk-product-gallery')) {
  customElements.define(
    'bk-product-gallery',
    class BkProductGallery extends HTMLElement {
      connectedCallback() {
        this.viewer = this.querySelector('.bk-gallery__viewer');
        this.track = this.querySelector('.bk-gallery__track');
        this.status = this.querySelector('[id^="BkGalleryStatus"]');
        this.slides = Array.from(this.querySelectorAll('.bk-gallery__slide'));
        this.thumbs = Array.from(this.querySelectorAll('.bk-gallery__thumb'));
        if (!this.track || this.slides.length === 0) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.viewer.addEventListener('keydown', this.onKeydown.bind(this));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(thumb.dataset.target)));

        this.strip = this.querySelector('.bk-gallery__thumbs');
        this.stripNav = Array.from(this.querySelectorAll('[data-thumbs-step]'));
        if (this.strip && this.stripNav.length) {
          this.onStripScroll = this.updateStripNav.bind(this);
          this.strip.addEventListener('scroll', this.onStripScroll, { passive: true });
          this.stripNav.forEach((btn) =>
            btn.addEventListener('click', () => {
              // Page by one full row of 4 thumbnails.
              this.strip.scrollBy({ left: Number(btn.dataset.thumbsStep) * this.strip.clientWidth, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
            })
          );
          // Width can change (or start at 0 inside a hidden container), so recheck which arrows apply.
          this.stripResize = new ResizeObserver(this.onStripScroll);
          this.stripResize.observe(this.strip);
          this.updateStripNav();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section) return;
            const mediaId = data.variant && data.variant.featured_media && data.variant.featured_media.id;
            if (mediaId) this.show(String(mediaId));
          });
        }
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        if (this.onStripScroll) this.strip?.removeEventListener('scroll', this.onStripScroll);
        this.stripResize?.disconnect();
        this.unsubscribe?.();
      }

      show(mediaId) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index < 0) return;
        this.scrollToIndex(index);
        this.setCurrent(index, true);
      }

      scrollToIndex(index) {
        const slide = this.slides[index];
        window.pauseAllMedia?.();
        this.track.scrollTo({ left: slide.offsetLeft, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
      }

      onScroll() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setCurrent(Math.min(Math.max(index, 0), this.slides.length - 1), false);
        });
      }

      setCurrent(index, announce) {
        if (this.current === index) return;
        this.current = index;
        this.thumbs.forEach((thumb, i) => {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.revealThumb(this.thumbs[index]);
        if (announce && this.status) this.status.textContent = `${index + 1} / ${this.slides.length}`;
      }

      // Scroll the strip only when the active thumbnail sits outside the visible row.
      revealThumb(thumb) {
        if (!thumb || !this.strip) return;
        const item = thumb.parentElement;
        const left = item.offsetLeft; // strip is position: relative, so this ignores its scroll position
        const right = left + item.offsetWidth;
        const viewLeft = this.strip.scrollLeft;
        const viewRight = viewLeft + this.strip.clientWidth;
        if (left < viewLeft) this.strip.scrollTo({ left });
        else if (right > viewRight + 1) this.strip.scrollTo({ left: right - this.strip.clientWidth });
      }

      updateStripNav() {
        const max = this.strip.scrollWidth - this.strip.clientWidth - 1;
        this.stripNav.forEach((btn) => {
          const atEdge = Number(btn.dataset.thumbsStep) < 0 ? this.strip.scrollLeft <= 0 : this.strip.scrollLeft >= max;
          // Keep keyboard focus in the strip when the focused arrow reaches its end.
          if (atEdge && document.activeElement === btn) this.stripNav.find((other) => other !== btn)?.focus();
          btn.disabled = atEdge;
        });
      }

      onKeydown(event) {
        if (event.target !== this.viewer) return;
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = Math.min(Math.max((this.current ?? 0) + step, 0), this.slides.length - 1);
        this.scrollToIndex(next);
        this.setCurrent(next, true);
      }
    }
  );
}
